import React, { useMemo } from 'react';
import {
  SimpleGrid,
  Card,
  CardBody,
  Text,
  Box,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { format, subMonths } from 'date-fns';

const STATUS_COLORS = {
  completed: '#38A169',
  in_progress: '#3182CE',
  pending: '#ECC94B',
  waiting_on_lab_results: '#805AD5',
  waiting_to_be_received: '#DD6B20',
  cancelled: '#E53E3E',
};

const DoctorCharts = ({ submissions, testTypeNames }) => {
  // Monthly submissions (last 6 months)
  const monthlyData = useMemo(() => {
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = subMonths(new Date(), 5 - i);
      return { month: format(d, 'MMM yyyy'), key: format(d, 'yyyy-MM'), count: 0 };
    });
    submissions.forEach((s) => {
      if (!s.submitted_at) return;
      const key = format(new Date(s.submitted_at), 'yyyy-MM');
      const found = months.find((m) => m.key === key);
      if (found) found.count++;
    });
    return months;
  }, [submissions]);

  // Status distribution
  const statusData = useMemo(() => {
    const counts = {};
    submissions.forEach((s) => {
      const st = s.status || 'unknown';
      counts[st] = (counts[st] || 0) + 1;
    });
    return Object.entries(counts).map(([status, count]) => ({
      name: status.replace(/_/g, ' '),
      value: count,
      color: STATUS_COLORS[status] || '#A0AEC0',
    }));
  }, [submissions]);

  // Top test types
  const testData = useMemo(() => {
    const counts = {};
    submissions.forEach((s) => {
      if (s.test_type_id) {
        const name = testTypeNames[s.test_type_id] || s.test_type_id;
        counts[name] = (counts[name] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name: name.length > 20 ? name.slice(0, 18) + '...' : name, count }));
  }, [submissions, testTypeNames]);

  if (submissions.length === 0) {
    return null;
  }

  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
      {/* Bar Chart: Monthly Submissions */}
      <Card bg="white" borderWidth="1px" borderColor="gray.200" shadow="sm">
        <CardBody>
          <Text fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
            Monthly Submissions
          </Text>
          <Box h="250px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3182CE" radius={[4, 4, 0, 0]} name="Submissions" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* Donut Chart: Status Distribution */}
      <Card bg="white" borderWidth="1px" borderColor="gray.200" shadow="sm">
        <CardBody>
          <Text fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
            Status Distribution
          </Text>
          <Box h="250px">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {statusData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* Line Chart: Trend */}
      <Card bg="white" borderWidth="1px" borderColor="gray.200" shadow="sm">
        <CardBody>
          <Text fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
            Submission Trend
          </Text>
          <Box h="250px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3182CE"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#3182CE' }}
                  name="Submissions"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* Horizontal Bar: Top Test Types */}
      <Card bg="white" borderWidth="1px" borderColor="gray.200" shadow="sm">
        <CardBody>
          <Text fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
            Top Test Types
          </Text>
          <Box h="250px">
            {testData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={testData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4299E1" radius={[0, 4, 4, 0]} name="Count" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Text fontSize="sm" color="gray.400" textAlign="center" pt={10}>
                No test data available
              </Text>
            )}
          </Box>
        </CardBody>
      </Card>
    </SimpleGrid>
  );
};

export default DoctorCharts;
